import { experimentsReady, getVariation } from '@jumpcloud/ui-experiments';
import { onboardingFeatures } from 'client/GrowthEngineering/utilities/GrowthConstants';
import Constants, { UserRoleConsts } from 'client/util/Constants';
import DateUtil from 'client/util/DateUtil';
import MspState from 'client/components/models/MspState';
import Util from 'client/util/Util';
import Vue from 'vue';

const growthModule = 'GrowthModule';
const settingsModule = 'SettingsModule';

const retryCallLimit = 10;
const retryInterval = 100; // milliseconds based on a fast API response

/**
 * A list of experiments. Used to compare against ui-experiments getVariation()
 * @property {String} flagName - The flagName of the experiment
 * @property {String} name - The name of the experiment. Used for event identification
 * @property {String} startDate - The date the experiment was started
 * @property {Boolean} showToOlderOrgs - Set to true to show the experiment to organizations
 * created before the experiment start date
 * @property {Object} variations - An object of the names of the possible variations a user may
 * be shown
 * @property {String | Function} eligibilityCheck - A string reference to a getter inside the
 * module or a function that doesn't require state information. Getters and Functions should
 * return Boolean value.
 */
const experiments = {
  // used in unit testing
  testExperiment: {
    flagName: 'NE-2411-test-experiment',
    showToOlderOrgs: true,
    startDate: '2023-06-22',
    variations: {
      original: 'original',
      variation: 'variant',
    },
  },

  remoteAssistOnboarding: {
    flagName: 'NE-2112-remote-assist-onboarding',
    startDate: '2023-03-02',
    variations: {
      original: 'original',
      variation: 'variant',
    },
    eligibilityCheck: 'getRemoteAssistOnboardingEligibility',
  },

  loginPromoNudgeExperiment: {
    flagName: 'PU-771-login-promo-nudge',
    startDate: '2023-08-08',
    variations: {
      original: 'original',
      variation: 'variation',
    },
  },
};

// --------------------------------------------------------------------------
// Private functions
// --------------------------------------------------------------------------
const getOrgCreateDateEligibility = (experiment, orgCreatedDate) => {
  try {
    if (!experiment || !experiment.startDate) return false;

    if (experiment.showToOlderOrgs) return true;

    const experimentStartDate = new Date(experiment.startDate);
    return !!DateUtil.isAfterDate(orgCreatedDate, experimentStartDate);
  } catch {
    // just return false and move on
    return false;
  }
};

// --------------------------------------------------------------------------
// Actions
// --------------------------------------------------------------------------
/**
 * Private action to get all active experiments and their current variants for org
 */
function fetchExperiments({
  commit, dispatch, getters, rootState,
}) {
  const { user } = rootState[settingsModule];
  const variations = {};

  Object.values(experiments).forEach(experiment => {
    const attributes = getters.getCustomAttributes(experiment);
    variations[experiment.flagName] = getVariation(
      experiment.flagName,
      attributes,
    );
  });

  // only update the data for admins, org api cannot be called for other roles
  if (
    user.roleName === UserRoleConsts.administrator
    || user.roleName === UserRoleConsts.administratorWithBilling
  ) {
    dispatch(`${growthModule}/updateExperimentStates`, variations, { root: true });
  }

  commit('SET_EXPERIMENTS', variations);
}

/**
 * Used to add all current experiments to state and growthData
 */
async function initializeExperiments({ commit, dispatch, state }) {
  let waitForUpdate = false;
  let retry = 0;

  const fetchingExperiments = setInterval(async () => {
    if (experimentsReady() && retry < retryCallLimit) {
      waitForUpdate = true;

      await dispatch('fetchExperiments');
      commit('SET_ARE_EXPERIMENTS_LOADED', true);

      clearInterval(fetchingExperiments);
    } else {
      retry += 1;
    }
  }, retryInterval);

  // wait for state to be updated or if fetching experiments fails then move on
  await new Promise((resolve) => {
    const experimentsStateUpdating = setInterval(async () => {
      if (!waitForUpdate || state.areExperimentsLoaded) {
        clearInterval(experimentsStateUpdating);
        resolve();
      }
    }, retryInterval);
  });
}

// --------------------------------------------------------------------------
// Getters
// --------------------------------------------------------------------------
/**
 * Private getter to get custom attributes to use for targeting of experiment
 */
function getCustomAttributes(state, getters, rootState, rootGetters) {
  return (experiment) => {
    const commandRunnerState = rootGetters['SettingsModule/isCommandRunner'];
    const orgCreatedDate = rootGetters['OrganizationsCollection/getOrgCreatedDate'];

    // for onboarding experiments for new orgs within 10 days from registration
    const isLessThan10DaysOld = Boolean(!DateUtil.moreThanDaysBeforeToday(orgCreatedDate, 10));

    // for onboarding experiments for new orgs within 30 days from registration
    const isLessThan30DaysOld = Boolean(!DateUtil.moreThanDaysBeforeToday(orgCreatedDate, 30));
    const isSmallScreen = Boolean(window.screen.width < 768);
    const { isAnMsp } = MspState;
    let isEligible = false;

    // orgs created after the start of experiments will be eligible
    const orgCreateDateEligibility = getOrgCreateDateEligibility(experiment, orgCreatedDate);

    // if no eligibilityCheck function, default to true
    const experimentEligibilityCheckFunction = experiment.eligibilityCheck;
    let passedEligibilityCheck = true;

    if (experimentEligibilityCheckFunction && typeof experimentEligibilityCheckFunction === 'string') {
      passedEligibilityCheck = getters[experimentEligibilityCheckFunction];
    } else if (experimentEligibilityCheckFunction && typeof experimentEligibilityCheckFunction === 'function') {
      passedEligibilityCheck = experimentEligibilityCheckFunction();
    }

    if (orgCreateDateEligibility && passedEligibilityCheck) {
      isEligible = true;
    }

    const attributes = {
      isAnMsp,
      isCommandRunner: commandRunnerState,
      isEligible,
      isLessThan10DaysOld,
      isLessThan30DaysOld,
      isSmallScreen,
    };

    return attributes;
  };
}

/**
 * Used to determine the current environment's Split API key, keys are available on Split.io
 * @returns {String} - Returns the key
 */
function getExperimentsEnvKey() {
  const envKeys = {
    [Constants.environments.local]: '68h91qrr26tm2ivm29847hr56759fpnaupla',
    [Constants.environments.staging]: 'on923ebs7ei306ar5dpa1f1i0i1oenl9csln',
    [Constants.environments.production]: '62kt7eedsr66l3nsqqamg11erv5dmmq7g6oa',
  };
  return envKeys[Util.getEnvironment()] || '';
}

/**
 * Gets the variation name of the given experiment
 * @param {String} experimentKey - key of experiment in the experiment const above
 * @returns {String} - Returns the key
 */
function getExperimentVariation(state) {
  return (experimentKey) => state.experiments[experiments[experimentKey]?.flagName] ?? '';
}

/**
 * Determines if the current experiment variation matches the given variation name
 * @param {String} experimentKey - key of experiment in the experiment const above
 * @param {String} variationsKey - key of variant in the variations object in the given experiment
 * @returns {Boolean} - Returns true if current experiment variation matches given variation name
 */
function getIsExperimentVariation(state) {
  return (experimentKey, variationsKey = 'variation') => {
    const currentVariation = state.experiments[experiments[experimentKey]?.flagName] ?? '';
    const variation = experiments[experimentKey].variations[variationsKey] ?? '';

    return !!(currentVariation && currentVariation === variation);
  };
}

/**
 * Private getter custom eligibility check for the Remote Assist Onboarding experiment
 * @returns {Boolean} - Returns true for eligible admins
 */
function getRemoteAssistOnboardingEligibility(state, getters, rootState, rootGetters) {
  const directOnboardingFeature = rootGetters['OnboardingModule/getDirectOnboardingFeature'];
  return directOnboardingFeature === onboardingFeatures.remoteAssist;
}

// --------------------------------------------------------------------------
// Mutations
// --------------------------------------------------------------------------
function SET_ARE_EXPERIMENTS_LOADED(state, isLoaded) {
  Vue.set(state, 'areExperimentsLoaded', isLoaded);
}

function SET_EXPERIMENTS(state, expVariations) {
  Vue.set(state, 'experiments', expVariations);
}

export const createExperimentsModule = () => ({
  namespaced: true,
  state: {
    experiments: {},
    areExperimentsLoaded: false,
  },

  actions: {
    fetchExperiments,
    initializeExperiments,
  },

  getters: {
    getCustomAttributes,
    getExperimentsEnvKey,
    getExperimentVariation,
    getIsExperimentVariation,
    getRemoteAssistOnboardingEligibility,
  },

  mutations: {
    SET_EXPERIMENTS,
    SET_ARE_EXPERIMENTS_LOADED,
  },
});

export default createExperimentsModule();
